import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Edit, Trash2, Search, Download, MapPin, BarChart, FileText } from 'lucide-react';
import { gerarRelatorioObrasFixas } from '../utils/reportGenerator';
import { useObrasFixas } from '../context/ObrasFixasContextSupabase';
import * as XLSX from 'xlsx';

const ListaObrasFixas = () => {
  const { obrasFixas, excluirObraFixa, filtrarObrasFixas } = useObrasFixas();
  const [filtros, setFiltros] = useState({
    termo: '',
    status: ''
  });

  const obrasFixasFiltradas = filtrarObrasFixas(filtros);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data) => {
    return new Date(data).toLocaleDateString('pt-BR');
  };
  
  // Formatar coordenadas para exibição
  const formatarCoordenadas = (latitude, longitude) => {
    if (!latitude || !longitude) return 'Não definido';
    return `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
  };
  
  // Formatar percentual
  const formatarPercentual = (valor) => {
    return `${valor.toFixed(2)}%`;
  };
  
  // Calcular valor total dos itens
  const calcularValorTotalItens = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => total + (item.total || 0), 0);
  };
  
  // Calcular progresso geral dos itens
  const calcularProgressoGeral = (itens) => {
    if (!itens || itens.length === 0) return 0;
    
    const totalValor = calcularValorTotalItens(itens);
    if (totalValor === 0) return 0;
    
    const totalMedido = itens.reduce((total, item) => total + (item.totalMedido || 0), 0);
    return (totalMedido / totalValor) * 100;
  };
  
  // Calcular a quantidade total medida (soma das quantidades medidas de todos os itens)
  const calcularQuantidadeTotalMedida = (itens) => {
    if (!itens || itens.length === 0) return 0;
    return itens.reduce((total, item) => total + (item.quantidadeMedida || 0), 0);
  };
  
  // Função para determinar a cor da barra de progresso
  const getProgressColor = (progresso) => {
    if (progresso < 30) return '#dc3545'; // Vermelho
    if (progresso < 70) return '#ffc107'; // Amarelo
    return '#28a745'; // Verde
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Planejada':
        return 'status-planejada';
      case 'Em execução':
        return 'status-execucao';
      case 'Finalizada':
        return 'status-finalizada';
      default:
        return '';
    }
  };

  const exportarParaExcel = () => {
    const dadosExport = obrasFixasFiltradas.map(obraFixa => {
      return {
        'Nome da Obra': obraFixa.nome,
        'Empreiteiro': obraFixa.empreiteiro,
        'Valor (R$)': obraFixa.valor,
        'Status': obraFixa.status,
        'Data de Início': formatarData(obraFixa.dataInicio),
        'Previsão de Término': formatarData(obraFixa.dataPrevisaoTermino),
        'Contrato': obraFixa.contrato || '',
        'Licitação': obraFixa.licitacao || '',
        'Processo': obraFixa.processo || '',
        'Gestor': obraFixa.gestor || '',
        'Fiscais de Obras': obraFixa.fiscaisObras || '',
        'Total Medições': calcularQuantidadeTotalMedida(obraFixa.itens || []),
        'Localização': obraFixa.localizacao ? formatarCoordenadas(obraFixa.localizacao.latitude, obraFixa.localizacao.longitude) : 'Não definido',
        'Nome do Local': obraFixa.localizacao ? obraFixa.localizacao.nome : '',
        'Observações': obraFixa.observacoes
      };
    });

    const ws = XLSX.utils.json_to_sheet(dadosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Obras Fixas');
    XLSX.writeFile(wb, 'obras-fixas-conceicao-castelo.xlsx');
  };

  const confirmarExclusao = (id, nome) => {
    if (window.confirm(`Tem certeza que deseja excluir a obra fixa "${nome}"?`)) {
      excluirObraFixa(id);
    }
  };
  
  // Gerar relatório em PDF
  const gerarRelatorioPDF = () => {
    gerarRelatorioObrasFixas(obrasFixasFiltradas, filtros);
  };

  // Função para calcular totalizadores por status
  const calcularTotalizadores = () => {
    const totais = {
      'Planejada': { obras: 0, valor: 0 },
      'Em execução': { obras: 0, valor: 0 },
      'Finalizada': { obras: 0, valor: 0 },
      'Total': { obras: 0, valor: 0 }
    };
    
    obrasFixas.forEach(obraFixa => {
      const status = obraFixa.status || 'Planejada';
      totais[status].obras += 1;
      totais[status].valor += parseFloat(obraFixa.valor || 0);
      
      // Adicionar ao total geral
      totais['Total'].obras += 1;
      totais['Total'].valor += parseFloat(obraFixa.valor || 0);
    });
    
    return totais;
  };
  
  const totalizadores = calcularTotalizadores();

  return (
    <div>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2>Lista de Obras Fixas</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="button" onClick={exportarParaExcel} style={{ backgroundColor: '#28a745' }}>
              <Download size={18} />
              Exportar Excel
            </button>
            <button className="button" onClick={gerarRelatorioPDF} style={{ backgroundColor: '#0d6efd' }}>
              <FileText size={18} />
              Relatório PDF
            </button>
          </div>
        </div>
        
        {/* Cards de Totalizadores */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '15px',
          marginBottom: '25px'
        }}>
          {/* Card Planejadas */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #ffeeba',
            borderLeft: '5px solid #ffc107'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#856404', fontSize: '16px' }}>Obras Planejadas</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Planejada'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Planejada'].valor)}</strong>
            </div>
          </div>
          
          {/* Card Em Execução */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #c3e6cb',
            borderLeft: '5px solid #28a745'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#155724', fontSize: '16px' }}>Em Execução</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Em execução'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Em execução'].valor)}</strong>
            </div>
          </div>
          
          {/* Card Finalizadas */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #b8daff',
            borderLeft: '5px solid #007bff'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#004085', fontSize: '16px' }}>Obras Finalizadas</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Finalizada'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Finalizada'].valor)}</strong>
            </div>
          </div>
          
          {/* Card Total */}
          <div style={{ 
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '15px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
            border: '1px solid #d6d8db',
            borderLeft: '5px solid #6c757d'
          }}>
            <h3 style={{ margin: '0 0 10px 0', color: '#383d41', fontSize: '16px' }}>Total Geral</h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Quantidade:</span>
              <strong>{totalizadores['Total'].obras}</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
              <span style={{ color: '#666' }}>Valor Total:</span>
              <strong>{formatarMoeda(totalizadores['Total'].valor)}</strong>
            </div>
          </div>
        </div>

        <div className="filters" style={{ display: 'grid', gridTemplateColumns: '3fr 1fr', gap: '15px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
            <input
              type="text"
              placeholder="Buscar por nome da obra ou empreiteiro..."
              value={filtros.termo}
              onChange={(e) => handleFiltroChange('termo', e.target.value)}
              style={{ paddingLeft: '40px', width: '100%' }}
            />
          </div>
          
          <select
            value={filtros.status}
            onChange={(e) => handleFiltroChange('status', e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">Todos os status</option>
            <option value="Planejada">Planejada</option>
            <option value="Em execução">Em execução</option>
            <option value="Finalizada">Finalizada</option>
          </select>
        </div>

        {obrasFixasFiltradas.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <p>Nenhuma obra fixa encontrada com os filtros aplicados.</p>
          </div>
        ) : (
          <div className="obras-grid">
            {obrasFixasFiltradas.map(obraFixa => (
              <div key={obraFixa.id} className="obra-card">
                <div style={{ display: 'flex', flexDirection: 'row', gap: '20px' }}>
                  {/* Coluna da esquerda - Informações principais */}
                  <div style={{ flex: 2 }}>
                    <div className="obra-header">
                      <div>
                        <h3 className="obra-title">{obraFixa.nome}</h3>
                        <span className={`obra-status ${getStatusClass(obraFixa.status)}`}>
                          {obraFixa.status}
                        </span>
                      </div>
                    </div>

                    <div className="obra-info" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      <div className="obra-info-item">
                        <strong>Empreiteiro:</strong> {obraFixa.empreiteiro}
                      </div>
                      <div className="obra-info-item">
                        <strong>Valor:</strong> {formatarMoeda(obraFixa.valor)}
                      </div>
                      <div className="obra-info-item">
                        <strong>Início:</strong> {formatarData(obraFixa.dataInicio)}
                      </div>
                      <div className="obra-info-item">
                        <strong>Previsão:</strong> {formatarData(obraFixa.dataPrevisaoTermino)}
                      </div>
                      {obraFixa.contrato && (
                        <div className="obra-info-item">
                          <strong>Contrato:</strong> {obraFixa.contrato}
                        </div>
                      )}
                      {obraFixa.licitacao && (
                        <div className="obra-info-item">
                          <strong>Licitação:</strong> {obraFixa.licitacao}
                        </div>
                      )}
                      {obraFixa.processo && (
                        <div className="obra-info-item">
                          <strong>Processo:</strong> {obraFixa.processo}
                        </div>
                      )}
                      <div className="obra-info-item">
                        <strong>Total Medições:</strong> {calcularQuantidadeTotalMedida(obraFixa.itens || [])} {obraFixa.itens && obraFixa.itens.length > 0 && obraFixa.itens[0].unidade ? obraFixa.itens[0].unidade : 'un'}
                      </div>
                    </div>
                    
                    {obraFixa.observacoes && (
                      <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                        <strong>Observações:</strong> {obraFixa.observacoes}
                      </div>
                    )}
                  </div>
                  
                  {/* Coluna do meio - Detalhes técnicos */}
                  <div style={{ flex: 2 }}>
                    {obraFixa.localizacao && (obraFixa.localizacao.latitude || obraFixa.localizacao.longitude) && (
                      <div style={{ 
                        marginBottom: '15px', 
                        padding: '8px 12px', 
                        backgroundColor: '#e3f2fd', 
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <MapPin size={16} style={{ color: '#0d47a1' }} />
                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#0d47a1' }}>
                          Localização: {formatarCoordenadas(obraFixa.localizacao.latitude, obraFixa.localizacao.longitude)}
                        </span>
                      </div>
                    )}
                    
                    {obraFixa.localizacao && obraFixa.localizacao.nome && (
                      <div style={{ 
                        marginBottom: '15px', 
                        padding: '8px 12px', 
                        backgroundColor: '#f3e5f5', 
                        borderRadius: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#6a1b9a' }}>
                          Local: {obraFixa.localizacao.nome}
                        </span>
                        {obraFixa.localizacao.descricao && (
                          <span style={{ fontSize: '12px', color: '#6a1b9a', marginLeft: 'auto' }}>
                            {obraFixa.localizacao.descricao}
                          </span>
                        )}
                      </div>
                    )}
                    
                    {obraFixa.itens && obraFixa.itens.length > 0 && (
                      <div>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          marginBottom: '5px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <BarChart size={16} style={{ color: '#0d6efd' }} />
                            <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#0d6efd' }}>
                              Progresso: {formatarPercentual(calcularProgressoGeral(obraFixa.itens))}
                            </span>
                          </div>
                          <span style={{ fontSize: '12px', color: '#0d6efd' }}>
                            {obraFixa.itens.length} item(ns)
                          </span>
                        </div>
                        
                        {/* Barra de Progresso */}
                        <div style={{ 
                          width: '100%', 
                          height: '10px', 
                          backgroundColor: '#e9ecef',
                          borderRadius: '5px',
                          overflow: 'hidden'
                        }}>
                          <div style={{ 
                            width: `${calcularProgressoGeral(obraFixa.itens)}%`, 
                            height: '100%', 
                            backgroundColor: getProgressColor(calcularProgressoGeral(obraFixa.itens)),
                            borderRadius: '5px'
                          }}></div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Coluna da direita - Ações */}
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-end' }}>
                    <div className="obra-actions" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      <Link to={`/obras-fixas/editar/${obraFixa.id}`} className="button secondary" style={{ width: '100%' }}>
                        <Edit size={16} />
                        Editar
                      </Link>
                      <Link to={`/obras-fixas/medicoes/${obraFixa.id}`} className="button" style={{ width: '100%', backgroundColor: '#0d6efd' }}>
                        <FileText size={16} />
                        Medições
                      </Link>
                      <button 
                        className="button danger"
                        onClick={() => confirmarExclusao(obraFixa.id, obraFixa.nome)}
                        style={{ width: '100%' }}
                      >
                        <Trash2 size={16} />
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ListaObrasFixas;